import { api, buildQueryParams, URLs } from '@/libs/api'
import type {
  ApiResponse,
  CreateApiResult,
  CursorPaginatedResponse,
  Invoice,
  InvoiceActionResult,
  InvoiceCreditPayload,
  InvoiceListData,
  InvoiceListParams,
  InvoicePayload,
  InvoicePaymentPayload,
  InvoicePaymentResult,
  InvoiceReminderPayload,
  InvoiceStatusPayload,
  InvoiceStatusResult,
  ListedInvoice,
} from '@/libs/interfaces'
import { invoicesApiMock } from '@/libs/mocks/handlers/billing'

type BillParams = { orgID: string; storeID: string; billID: string }

const buildListQuery = (params: InvoiceListParams): string =>
  buildQueryParams({
    search: params.search,
    limit: params.limit,
    cursor: params.cursor,
    prev: params.prev,
    sortBy: params.sortBy,
    sortDirection: params.sortDirection,
    billStatus: params.billStatus,
    from: params.from,
    to: params.to,
  })

// The list endpoint names the amount `billed`/`billedCurrency`; normalise it onto the
// `billedAmount`/`currency` fields everything else (GET-by-ID, forms, exports) reads.
const toInvoice = ({ billed, billedCurrency, ...invoice }: ListedInvoice): Invoice => ({
  ...invoice,
  billedAmount: invoice.billedAmount ?? billed,
  currency: invoice.currency ?? billedCurrency,
})

const invoicesApiReal = {
  create: async (
    params: { orgID: string; storeID: string },
    payload: InvoicePayload,
  ): Promise<CreateApiResult<Invoice>> => {
    // POST responds with the created bill object directly, same as GET by ID.
    return api
      .post<Invoice>(URLs.Bills.create(params.orgID, params.storeID), payload)
      .then((response) => ({
        data: response.data,
        status: response.status,
        message: response.message,
      }))
  },

  getByID: async (params: { orgID: string; storeID: string; billID: string }): Promise<Invoice> => {
    return api
      .get<Invoice>(URLs.Bills.getByID(params.storeID, params.billID))
      .then((response) => response.data)
  },

  list: async (params: InvoiceListParams): Promise<Invoice[]> => {
    return api
      .get<ApiResponse<InvoiceListData>>(URLs.Bills.list(params.storeID, buildListQuery(params)))
      .then((response) => (response.data?.data.bills ?? []).map(toInvoice))
  },

  listCursor: async (params: InvoiceListParams): Promise<CursorPaginatedResponse<Invoice>> => {
    const response = await api
      .get<ApiResponse<InvoiceListData>>(URLs.Bills.list(params.storeID, buildListQuery(params)))
      .then((response) => response?.data?.data)

    return {
      data: response.bills.map(toInvoice),
      pageInfo: {
        first: response.first,
        prev: response.prev,
        cursor: response.cursor,
        next: response.next,
        last: response.last,
      },
    }
  },

  update: async (
    params: { orgID: string; storeID: string; billID: string },
    payload: Partial<InvoicePayload>,
  ): Promise<Invoice> => {
    return api
      .patch<Invoice>(URLs.Bills.byID(params.orgID, params.storeID, params.billID), payload)
      .then((response) => response.data)
  },

  delete: async (params: { orgID: string; storeID: string; billID: string }): Promise<Invoice> => {
    return api
      .delete<Invoice>(URLs.Bills.byID(params.orgID, params.storeID, params.billID))
      .then((response) => response.data)
  },

  updateStatus: async (
    params: { storeID: string; billID: string },
    payload: InvoiceStatusPayload,
  ): Promise<InvoiceActionResult<InvoiceStatusResult>> => {
    return api
      .patch<{
        message?: string
        data: InvoiceStatusResult
      }>(URLs.Bills.status(params.storeID, params.billID), payload)
      .then((response) => ({ data: response.data.data, message: response.data.message }))
  },

  registerPayment: async (
    params: BillParams,
    payload: InvoicePaymentPayload,
  ): Promise<InvoiceActionResult<InvoicePaymentResult>> => {
    return api
      .post<{
        message?: string
        data: InvoicePaymentResult
      }>(URLs.Bills.payments(params.orgID, params.storeID, params.billID), payload)
      .then((response) => ({ data: response.data.data, message: response.data.message }))
  },

  /** Issues a credit invoice; resolves with the new credit invoice. */
  createCredit: async (params: BillParams, payload: InvoiceCreditPayload): Promise<Invoice> => {
    return api
      .post<Invoice>(URLs.Bills.credit(params.orgID, params.storeID, params.billID), payload)
      .then((response) => response.data)
  },

  /** Issues a reminder invoice; resolves with the new reminder invoice. */
  createReminderInvoice: async (
    params: BillParams,
    payload: InvoiceReminderPayload,
  ): Promise<Invoice> => {
    return api
      .post<Invoice>(
        URLs.Bills.reminderInvoice(params.orgID, params.storeID, params.billID),
        payload,
      )
      .then((response) => response.data)
  },

  sendEmail: async (params: BillParams): Promise<InvoiceActionResult<undefined>> => {
    return api
      .post<{ message?: string }>(URLs.Bills.email(params.orgID, params.storeID, params.billID))
      .then((response) => ({ data: undefined, message: response.data?.message }))
  },

  sendReminder: async (params: {
    storeID: string
    billID: string
  }): Promise<InvoiceActionResult<undefined>> => {
    return api
      .post<{ message?: string }>(URLs.Bills.remind(params.storeID, params.billID))
      .then((response) => ({ data: undefined, message: response.data?.message }))
  },
}

export const invoicesApi: typeof invoicesApiReal = __IS_MOCK_MODE__
  ? invoicesApiMock
  : invoicesApiReal
